import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Loader2, UserX, Users, AlertCircle, Edit, Shield, Building2, Key } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_INFO = {
  admin: { color: 'bg-[#C85A40]/10 text-[#C85A40]', description: 'Full system access' },
  manager: { color: 'bg-[#3E3F96]/10 text-[#3E3F96]', description: 'Reports, Discounts initiator, Bank Recon' },
  accountant: { color: 'bg-[#5556A8]/10 text-[#5556A8]', description: 'Invoices, Payments, Students, Call Logs, Discount initiator, Bank Recon' },
  admission_incharge: { color: 'bg-[#D99C3B]/10 text-[#D99C3B]', description: 'Student management only' },
  accountant_assistant: { color: 'bg-[#6B7280]/10 text-[#6B7280]', description: 'Reports, Bank entries, Cash & bank receipts, Students, Outstanding, Credit Note initiator' },
  field_supervisor: { color: 'bg-[#10B981]/10 text-[#10B981]', description: 'Lunch & Club verification only' }
};

const BRANCHES = ['KISAASI', 'BUNGA', 'KIGALI', 'LUBOWA'];

const UsersPage = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Password reset state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'accountant',
    branches: []
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, systemRes, rolesRes] = await Promise.all([
        usersApi.getAll(),
        configApi.getSystemInfo(),
        api.get('/roles')
      ]);
      setUsers(usersRes.data);
      setSystemInfo(systemRes.data);
      setRoles(rolesRes.data.roles || []);
    } catch (err) {
      toast.error('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [fetchData, isAdmin]);

  const handleBranchToggle = (branch) => {
    const current = newUser.branches;
    if (current.includes(branch)) {
      setNewUser({ ...newUser, branches: current.filter(b => b !== branch) });
    } else {
      setNewUser({ ...newUser, branches: [...current, branch] });
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    // Validate branches for non-admin roles
    if (newUser.role !== 'admin' && newUser.branches.length === 0) {
      toast.error('Please select at least one branch for this role');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingUser) {
        await usersApi.update(editingUser.id, {
          name: newUser.name,
          role: newUser.role,
          branches: newUser.branches
        });
        toast.success('User updated successfully');
      } else {
        await usersApi.create({
          email: newUser.email,
          password: newUser.password,
          name: newUser.name,
          role: newUser.role,
          branches: newUser.branches
        });
        toast.success('User created successfully');
      }
      setShowAddDialog(false);
      setEditingUser(null);
      setNewUser({ email: '', password: '', name: '', role: 'accountant', branches: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewUser({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      branches: user.branches || (user.branch ? [user.branch] : [])
    });
    setShowAddDialog(true);
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('User deactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingUser(null);
    setNewUser({ email: '', password: '', name: '', role: 'accountant', branches: [] });
  };

  const handleOpenPasswordReset = (user) => {
    setPasswordResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPasswordResetUser(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await usersApi.resetPassword(passwordResetUser.id, newPassword);
      toast.success(`Password reset for ${passwordResetUser.name}`);
      handleClosePasswordDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-[#C85A40] mb-4" />
        <p className="text-[#5C635F]">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">User Management</h1>
          <p className="text-[#5C635F] mt-1">Manage system users and role-based access</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={(open) => open ? setShowAddDialog(true) : handleCloseDialog()}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#3E3F96] hover:bg-[#2D2E7A]" 
              disabled={systemInfo && systemInfo.current_users >= systemInfo.max_users}
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={newUser.name} 
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} 
                  required 
                  data-testid="user-name-input" 
                />
              </div>

              {!editingUser && (
                <>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      type="email" 
                      value={newUser.email} 
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} 
                      required 
                      data-testid="user-email-input" 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input 
                      type="password" 
                      value={newUser.password} 
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                      required 
                      minLength={6} 
                      data-testid="user-password-input" 
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v, branches: v === 'admin' ? [] : newUser.branches })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(ROLE_INFO).map((role) => (
                      <SelectItem key={role} value={role}>
                        <div className="flex flex-col">
                          <span className="capitalize">{role.replace(/_/g, ' ')}</span>
                          <span className="text-xs text-[#5C635F]">{ROLE_INFO[role].description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Branch Selection - Only for non-admin roles */}
              {newUser.role !== 'admin' && (
                <div className="space-y-2">
                  <Label>Branches (Required)</Label>
                  <p className="text-xs text-[#5C635F]">User can only access data from assigned branches</p>
                  <div className="grid grid-cols-2 gap-2 p-3 bg-[#F5F5FA] rounded-lg">
                    {BRANCHES.map((branch) => (
                      <div key={branch} className="flex items-center gap-2">
                        <Checkbox 
                          id={`branch-${branch}`}
                          checked={newUser.branches.includes(branch)}
                          onCheckedChange={() => handleBranchToggle(branch)}
                        />
                        <label htmlFor={`branch-${branch}`} className="text-sm cursor-pointer">
                          {branch}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Role Info Card */}
              {newUser.role && ROLE_INFO[newUser.role] && (
                <Card className="border-[#D8D9E8] bg-[#F5F5FA]">
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-[#3E3F96] mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-[#1C1F1D]">
                          {newUser.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Access
                        </p>
                        <p className="text-xs text-[#5C635F]">{ROLE_INFO[newUser.role].description}</p>
                        {newUser.role !== 'admin' && (
                          <p className="text-xs text-[#D99C3B] mt-1">Branch-locked: User sees only assigned branch data</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-user-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingUser ? 'Update' : 'Create'} User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* System Info */}
      {systemInfo && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#3E3F96]/10 rounded-lg">
                  <Users className="w-5 h-5 text-[#3E3F96]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">Total Users</p>
                  <p className="text-xl font-bold text-[#1C1F1D]">{systemInfo.current_users} / {systemInfo.max_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-[#D8D9E8] ${(systemInfo.max_users - systemInfo.current_users) <= 2 ? 'border-l-4 border-l-[#D99C3B]' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#D99C3B]/10 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-[#D99C3B]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">Remaining Slots</p>
                  <p className="text-xl font-bold text-[#1C1F1D]">{systemInfo.max_users - systemInfo.current_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#5556A8]/10 rounded-lg">
                  <Shield className="w-5 h-5 text-[#5556A8]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">License Expiry</p>
                  <p className="text-sm font-bold text-[#1C1F1D]">{systemInfo.license_expiry || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Role Legend */}
      <Card className="border-[#D8D9E8]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Roles & Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(ROLE_INFO).map(([role, info]) => (
              <div key={role} className="p-2 rounded-lg border border-[#D8D9E8]">
                <Badge className={`${info.color} text-xs`}>{role.replace(/_/g, ' ')}</Badge>
                <p className="text-[10px] text-[#5C635F] mt-1">{info.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-[#D8D9E8]">
        <CardHeader>
          <CardTitle className="text-lg">All Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5FA]">
                  <TableHead className="text-xs uppercase font-semibold">Name</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Email</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Role</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Branches</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell className="text-[#5C635F]">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={ROLE_INFO[user.role]?.color || 'bg-gray-100 text-gray-600'}>
                        {user.role?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <span className="text-sm text-[#5C635F]">All Branches</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(user.branches || (user.branch ? [user.branch] : [])).map((branch) => (
                            <Badge key={branch} variant="outline" className="text-xs">{branch}</Badge>
                          ))}
                          {(!user.branches || user.branches.length === 0) && !user.branch && (
                            <span className="text-xs text-[#C85A40]">No branch</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditUser(user)}
                          data-testid={`edit-user-${user.id}`}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleOpenPasswordReset(user)}
                          data-testid={`reset-password-${user.id}`}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4 text-[#D99C3B]" />
                        </Button>
                        {user.is_active && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#C85A40]" 
                            onClick={() => handleDeactivate(user.id)}
                            data-testid={`deactivate-user-${user.id}`}
                            title="Deactivate User"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => !open && handleClosePasswordDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          {passwordResetUser && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="p-3 bg-[#F5F5FA] rounded-lg">
                <p className="text-sm text-[#5C635F]">Resetting password for:</p>
                <p className="font-medium text-[#1C1F1D]">{passwordResetUser.name}</p>
                <p className="text-sm text-[#5C635F]">{passwordResetUser.email}</p>
              </div>
              
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  data-testid="admin-new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Re-enter password"
                  data-testid="admin-confirm-password-input"
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-[#C85A40] flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Passwords do not match
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={handleClosePasswordDialog}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#3E3F96] hover:bg-[#2D2E7A]" 
                  disabled={resettingPassword || newPassword !== confirmPassword}
                  data-testid="admin-reset-password-btn"
                >
                  {resettingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Reset Password
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
